// ─────────────────────────────────────────────────────────────
// poPrint.config.js — Report 1: single Purchase Order print
// Shape: document (landscape)
// Data:  GET /api/tape/purchase-orders/:id  (header + items[])
// ─────────────────────────────────────────────────────────────

import { TERMS_PO, SIGNATURES, FORMATTERS } from '../../print/printConfig';

const DELIVERY_LABEL = {
  ex_works: 'Ex-works (we pay freight)',
  delivered: 'Delivered (vendor pays freight)',
};

const STATUS_LABEL = {
  pending: 'Pending',
  partial: 'Partially Received',
  received: 'Received',
  cancelled: 'Cancelled',
};

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

export const poPrintConfig = (po) => {
  const items = po.items || [];

  const gstAmount = num(po.gst_amount);
  const total = num(po.total_amount);
  const subtotal = total - gstAmount;

  const gstLabel =
    po.gst_mode === 'percent' && num(po.gst_value) > 0
      ? `GST (${num(po.gst_value)}%)`
      : 'GST';

  const rows = items.map((it) => ({
    jumbo: it.jumbo_name,
    spec: `${FORMATTERS.int(it.default_meters)} m · ${it.width_inches}" · core ${it.core_length_meters} m`,
    uom: 'Roll',
    quantity: it.quantity,
    weight_per_roll_kg: it.weight_per_roll_kg,
    rate_per_kg: it.rate_per_kg,
    rate: it.rate,
    amount: it.amount,
  }));

  const totalRolls = items.reduce((s, it) => s + num(it.quantity), 0);
  const totalKg = items.reduce(
    (s, it) => s + num(it.quantity) * num(it.weight_per_roll_kg),
    0
  );

  return {
    kind: 'document',
    orientation: 'l',
    title: 'Purchase Order',

    boxes: [
      {
        heading: 'Vendor Details',
        rows: [
          { label: 'Vendor Name', value: po.vendor_name },
          { label: 'Address', value: po.vendor_address },
          { label: 'Contact Person', value: po.vendor_contact_person },
          { label: 'Phone', value: po.vendor_phone },
          { label: 'NTN / STRN', value: `${po.vendor_ntn || '—'}  /  ${po.vendor_strn || '—'}` },
        ],
      },
      {
        heading: 'Purchase Order Details',
        rows: [
          {
            label: 'PO Date',
            value: FORMATTERS.date(po.po_date),
            label2: 'PO #',
            value2: po.po_number,
          },
          {
            label: 'Status',
            value: STATUS_LABEL[po.status] || po.status,
            label2: 'Lines',
            value2: items.length,
          },
          {
            label: 'Delivery',
            value: DELIVERY_LABEL[po.delivery_terms] || '—',
          },
          {
            label: 'Pay Terms',
            value: po.vendor_payment_terms_days
              ? `${po.vendor_payment_terms_days} days`
              : 'On delivery',
            label2: 'Total Rolls',
            value2: FORMATTERS.qty(totalRolls),
          },
          { label: 'Notes', value: po.notes },
        ],
      },
    ],

    columns: [
      { key: 'jumbo', label: 'Jumbo', align: 'left', fmt: 'text', width: 40 },
      { key: 'spec', label: 'Specification', align: 'left', fmt: 'text' },
      { key: 'uom', label: 'Uom', align: 'center', fmt: 'text', width: 14 },
      { key: 'quantity', label: 'Qty', align: 'right', fmt: 'qty', width: 16 },
      { key: 'weight_per_roll_kg', label: 'Wt / Roll', align: 'right', fmt: 'kg', width: 22 },
      { key: 'rate_per_kg', label: 'Rate / kg', align: 'right', fmt: 'rate', width: 22 },
      { key: 'rate', label: 'Rate / Roll', align: 'right', fmt: 'money', width: 24 },
      { key: 'amount', label: 'Amount', align: 'right', fmt: 'money', width: 28, bold: true },
    ],

    rows,

    footRow: [
      'TOTAL',
      '',
      '',
      FORMATTERS.qty(totalRolls),
      FORMATTERS.kg(totalKg),
      '',
      '',
      FORMATTERS.money(subtotal),
    ],

    totals: [
      { label: 'Goods Subtotal', value: subtotal, fmt: 'money' },
      { label: gstLabel, value: gstAmount, fmt: 'money' },
      { label: 'Grand Total', value: total, fmt: 'money', emphasis: true, tone: 'net' },
    ],

    terms: TERMS_PO,
    signatures: SIGNATURES.po,
  };
};

export default poPrintConfig;